import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import useAuthentication from "../../hooks/useAuthentication";

/**
 * Reusable WeClapp Customer Selector Component
 *
 * Props:
 * - isOpen: boolean - Whether the modal is open
 * - onClose: function - Callback when modal is closed
 * - onSelect: function(customer) - Callback when a customer is selected
 * - title: string (optional) - Custom modal title
 */
const WeClappCustomerSelector = ({ isOpen, onClose, onSelect, title = "WeClapp-Kunden auswählen" }) => {
    const Auth = useAuthentication();

    const [weclappCustomers, setWeclappCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [customerSearchInput, setCustomerSearchInput] = useState("");
    const [filteredWeclappCustomers, setFilteredWeclappCustomers] = useState([]);

    useEffect(() => {
        // Load customers when modal opens
        if (isOpen && weclappCustomers.length === 0) {
            loadWeClappCustomers();
        }
    }, [isOpen]);

    useEffect(() => {
        // Filter WeClapp customers based on search input
        if (customerSearchInput) {
            const searchLower = customerSearchInput.toLowerCase();
            setFilteredWeclappCustomers(weclappCustomers.filter(customer => {
                const customerName = getWeClappCustomerName(customer).toLowerCase();
                const customerNumber = (customer.customerNumber || '').toLowerCase();
                const email = (customer.email || '').toLowerCase();
                return customerName.includes(searchLower) ||
                       customerNumber.includes(searchLower) ||
                       email.includes(searchLower);
            }));
        } else {
            setFilteredWeclappCustomers(weclappCustomers);
        }
    }, [customerSearchInput, weclappCustomers]);

    const loadWeClappCustomers = async () => {
        setLoadingCustomers(true);
        let allResults = [];
        let currentPage = 1;
        const pageSize = 500;
        let hasMore = true;

        try {
            while (hasMore) {
                const response = await axios({
                    method: "get",
                    url: SyncConstants["syncBaseURL"] + "weclapp/customers",
                    auth: Auth.getAuthObject(),
                    params: {
                        page: currentPage,
                        pageSize: pageSize
                    }
                });

                if (response.data.success && response.data.data.result) {
                    const results = response.data.data.result;
                    allResults = [...allResults, ...results];

                    if (results.length < pageSize) {
                        hasMore = false;
                    } else {
                        currentPage++;
                    }
                } else {
                    hasMore = false;
                }

                if (currentPage > 50) {
                    hasMore = false;
                }
            }

            setWeclappCustomers(allResults);
            setFilteredWeclappCustomers(allResults);
        } catch (error) {
            console.error("Error loading WeClapp customers:", error);
        } finally {
            setLoadingCustomers(false);
        }
    };

    const getWeClappCustomerName = (customer) => {
        if (customer.company) return customer.company;
        if (customer.firstName && customer.lastName) {
            return `${customer.firstName} ${customer.lastName}`;
        }
        return customer.customerNumber || 'Unbekannt';
    };

    const selectWeClappCustomer = (customer) => {
        setCustomerSearchInput("");
        onSelect(customer);
    };

    const handleClose = () => {
        setCustomerSearchInput("");
        onClose();
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div
            className="modal d-block"
            style={{
                backgroundColor: 'rgba(0,0,0,0.5)',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    handleClose();
                }
            }}
        >
            <div className="modal-dialog modal-xl" style={{ margin: '0 auto', maxWidth: '1140px' }}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{title}</h5>
                        <button type="button" className="btn-close" onClick={handleClose}></button>
                    </div>
                    <div className="modal-body">
                        {loadingCustomers ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Lade Kunden...</span>
                                </div>
                                <p className="mt-3">Lade alle Kunden aus WeClapp...</p>
                            </div>
                        ) : (
                            <>
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Nach Kunden suchen (Name, Kundennummer, E-Mail...)"
                                        value={customerSearchInput}
                                        onChange={e => setCustomerSearchInput(e.target.value)}
                                        autoFocus
                                    />
                                    <small className="text-muted">
                                        {weclappCustomers.length} Kunden geladen
                                        {customerSearchInput && ` - ${filteredWeclappCustomers.length} Treffer`}
                                    </small>
                                </div>

                                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                    <table className="table table-hover">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th>Kundennr.</th>
                                                <th>Name</th>
                                                <th>E-Mail</th>
                                                <th>Stadt</th>
                                                <th style={{width: '100px'}}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredWeclappCustomers.slice(0, 50).map(customer => (
                                                <tr key={customer.id}>
                                                    <td>
                                                        <span className="badge bg-light text-dark border">
                                                            {customer.customerNumber || '-'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <strong>{getWeClappCustomerName(customer)}</strong>
                                                        {customer.company2 && (
                                                            <div className="small text-muted">{customer.company2}</div>
                                                        )}
                                                    </td>
                                                    <td className="small">{customer.email || '-'}</td>
                                                    <td className="small">
                                                        {customer.addresses && customer.addresses[0]
                                                            ? customer.addresses[0].city
                                                            : '-'}
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() => selectWeClappCustomer(customer)}
                                                        >
                                                            <i className="bi bi-check-lg"></i> Auswählen
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {filteredWeclappCustomers.length > 50 && (
                                        <div className="alert alert-info">
                                            Es werden nur die ersten 50 Treffer angezeigt. Nutzen Sie die Suche, um die Ergebnisse einzugrenzen.
                                        </div>
                                    )}
                                    {filteredWeclappCustomers.length === 0 && (
                                        <div className="alert alert-warning">
                                            Keine Kunden gefunden, die "{customerSearchInput}" entsprechen.
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={handleClose}>
                            Abbrechen
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default WeClappCustomerSelector;
